import React from 'react'
import { motion } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'
import { FigmaCard, FigmaButton } from '../components/FigmaUIElements'
import { MapPin, Compass, Star, Search, Eye, Rocket } from 'lucide-react'

const Exploration: React.FC = () => {
  const { settings } = useAnimations()

  return (
    <motion.div
      className="min-h-screen p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-8"
          animate={settings.floatingElements ? {
            y: [0, -5, 0],
            transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
          } : {}}
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent mb-4">
            Deep Space Exploration
          </h1>
          <p className="text-gray-300 text-lg">
            Venture into the unknown. Discover new worlds, resources, and civilizations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <MapPin className="w-8 h-8 text-cyan-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Sectors Explored</h3>
            </div>
            <div className="text-3xl font-bold text-cyan-400 mb-2">847</div>
            <p className="text-gray-300">Out of 10,000+ known sectors</p>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Star className="w-8 h-8 text-yellow-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Discoveries</h3>
            </div>
            <div className="text-3xl font-bold text-yellow-400 mb-2">23</div>
            <p className="text-gray-300">Unique findings this month</p>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Eye className="w-8 h-8 text-purple-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Active Probes</h3>
            </div>
            <div className="text-3xl font-bold text-purple-400 mb-2">15</div>
            <p className="text-gray-300">Currently scanning deep space</p>
          </FigmaCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Unexplored Regions</h3>
            <div className="space-y-4">
              {[
                {
                  name: 'Nebula Sector X-7',
                  distance: '450 ly',
                  scanLevel: 'Preliminary',
                  resources: 'Unknown',
                  difficulty: 'High',
                  estimatedTime: '18h'
                },
                {
                  name: 'Binary System Theta',
                  distance: '280 ly',
                  scanLevel: 'Partial',
                  resources: 'Rare Metals Detected',
                  difficulty: 'Medium',
                  estimatedTime: '12h'
                },
                {
                  name: 'Dark Matter Anomaly',
                  distance: '650 ly',
                  scanLevel: 'None',
                  resources: 'Exotic Matter',
                  difficulty: 'Extreme',
                  estimatedTime: '36h'
                }
              ].map((region, index) => (
                <motion.div
                  key={region.name}
                  className="p-4 bg-gray-800/50 rounded-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">{region.name}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      region.difficulty === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      region.difficulty === 'High' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {region.difficulty}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <span className="text-gray-400">Distance: </span>
                      <span className="text-white">{region.distance}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Scan: </span>
                      <span className="text-cyan-400">{region.scanLevel}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Resources: </span>
                      <span className="text-white">{region.resources}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">ETA: </span>
                      <span className="text-white">{region.estimatedTime}</span>
                    </div>
                  </div>
                  <FigmaButton variant="primary" className="w-full">
                    Launch Expedition
                  </FigmaButton>
                </motion.div>
              ))}
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Recent Discoveries</h3>
            <div className="space-y-4 mb-6">
              {[
                {
                  name: 'Crystalline Planet Zeta-9',
                  type: 'Planetary Body',
                  value: 'High',
                  description: 'Rich in exotic crystals with unique energy properties',
                  date: '3 days ago'
                },
                {
                  name: 'Ancient Ruins Complex',
                  type: 'Archaeological Site',
                  value: 'Extreme',
                  description: 'Pre-stellar civilization artifacts discovered',
                  date: '1 week ago'
                },
                {
                  name: 'Quantum Resonance Field',
                  type: 'Spatial Anomaly',
                  value: 'Research',
                  description: 'Unique quantum phenomena affecting space-time',
                  date: '2 weeks ago'
                }
              ].map((discovery, index) => (
                <motion.div
                  key={discovery.name}
                  className="p-4 bg-gradient-to-r from-gray-800/30 to-gray-800/50 rounded-lg border-l-4 border-cyan-400"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">{discovery.name}</h4>
                    <span className="text-gray-400 text-xs">{discovery.date}</span>
                  </div>
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-cyan-400 text-sm">{discovery.type}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      discovery.value === 'Extreme' ? 'bg-purple-500/20 text-purple-400' :
                      discovery.value === 'High' ? 'bg-green-500/20 text-green-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {discovery.value} Value
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm">{discovery.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="border-t border-gray-700 pt-6">
              <h4 className="text-lg font-medium text-white mb-4">Exploration Tools</h4>
              <div className="grid grid-cols-2 gap-4">
                <FigmaButton variant="secondary" className="flex items-center justify-center">
                  <Search className="w-4 h-4 mr-2" />
                  Deep Scan
                </FigmaButton>
                <FigmaButton variant="secondary" className="flex items-center justify-center">
                  <Compass className="w-4 h-4 mr-2" />
                  Set Course
                </FigmaButton>
                <FigmaButton variant="secondary" className="flex items-center justify-center">
                  <Rocket className="w-4 h-4 mr-2" />
                  Launch Probe
                </FigmaButton>
                <FigmaButton variant="secondary" className="flex items-center justify-center">
                  <Eye className="w-4 h-4 mr-2" />
                  Observatory
                </FigmaButton>
              </div>
            </div>
          </FigmaCard>
        </div>
      </div>
    </motion.div>
  )
}

export default Exploration